import type { RouteLocationAsRelativeGeneric } from 'vue-router'
import IconBatteryBank from '@/assets/icons/battery_bank.svg'
import IconBatteryType from '@/assets/icons/battery_type.svg'
import IconBiogas from '@/assets/icons/biogas.svg'
import IconEnergyConsumer from '@/assets/icons/energy_consumer.svg'
import IconFuel from '@/assets/icons/fuel.svg'
import IconGenerator from '@/assets/icons/generator.svg'
import IconHydro from '@/assets/icons/hydro.svg'
import IconPowerGrid from '@/assets/icons/power_grid.svg'
import IconSolarPv from '@/assets/icons/solar_pv.svg'
import IconWind from '@/assets/icons/wind.svg'
import i18n from '@/i18n'
import { AssetFamilyTypeId } from '@/types/hierarchy/asset-family-type-id.enum'
import { AssetFamilyTypeBatteryBanks } from '@/types/hierarchy/asset-family-type-parents.enum'
import { AssetFamilyTypeRoutes } from '@/types/hierarchy/asset-family-type-routes.enum'
import { isBatteryBank } from '@/utils/battery-bank'
import { ASSET_COLORS } from '@/utils/constants/colors'
import { isEnergyConsumer } from '@/utils/energy-consumer'

const COLOR_STEP_PERCENTAGE = 5
const COLOR_VARIATION_THRESHOLD = 2

export function getAssetFamilyTypeChartColor(assetFamilyTypeId: string, assetFamilyIndex: number): string {
  const stepDirection = assetFamilyIndex > COLOR_VARIATION_THRESHOLD ? '+' : '-'

  const calculatedIndex = assetFamilyIndex > COLOR_VARIATION_THRESHOLD ? assetFamilyIndex - COLOR_VARIATION_THRESHOLD : assetFamilyIndex

  const baseColor = getAssetFamilyTypeColor(assetFamilyTypeId)

  // Adjusts lightness in LCH while preserving chroma and hue
  // to create related color variations per index
  const calculatedChange = `calc(l ${stepDirection} ${calculatedIndex * COLOR_STEP_PERCENTAGE}) c h`
  const processedColor = `lch(from ${baseColor} ${calculatedChange})`

  return processedColor
}

// Returns a function that hands out a variation index per base color, so only
// series sharing the same base color (e.g. multiple battery banks) get shifted.
// Create a new indexer each time a chart's series are rebuilt.
export function createAssetFamilyTypeColorIndexer(): (assetFamilyTypeId: string) => number {
  const indexes = new Map<string, number>()

  return (assetFamilyTypeId: string) => {
    const baseColor = getAssetFamilyTypeColor(assetFamilyTypeId)
    const index = indexes.get(baseColor) ?? 0
    indexes.set(baseColor, index + 1)
    return index
  }
}

export function getAssetFamilyTypeColor(assetFamilyTypeId: string): string {
  switch (assetFamilyTypeId) {
    case AssetFamilyTypeId.Biogas:
      return ASSET_COLORS.BIOGAS
    case AssetFamilyTypeId.Generator:
      return ASSET_COLORS.GENERATOR
    case AssetFamilyTypeId.Hydro:
      return ASSET_COLORS.HYDRO
    case AssetFamilyTypeId.PowerGrid:
      return ASSET_COLORS.POWER_GRID
    case AssetFamilyTypeId.SolarPv:
      return ASSET_COLORS.SOLAR_PV
    case AssetFamilyTypeId.Wind:
      return ASSET_COLORS.WIND
    default:
      return isEnergyConsumer(assetFamilyTypeId as AssetFamilyTypeId)
        ? ASSET_COLORS.ENERGY_CONSUMER
        : isBatteryBank(assetFamilyTypeId as AssetFamilyTypeId)
          ? ASSET_COLORS.BATTERY_BANK
          : ASSET_COLORS.BATTERY
  }
}

export function getAssetFamilyTypeIcon(assetFamilyTypeId: string): string {
  switch (assetFamilyTypeId) {
    case AssetFamilyTypeId.BatteryBankFlooded:
    case AssetFamilyTypeId.BatteryBankVRLA:
    case AssetFamilyTypeId.BatteryBankLFP:
    case AssetFamilyTypeId.BatteryBankNMC:
      return IconBatteryBank
    case AssetFamilyTypeId.Biogas:
      return IconBiogas
    case AssetFamilyTypeId.ResidentialEnergyConsumer:
    case AssetFamilyTypeId.CommercialEnergyConsumer:
    case AssetFamilyTypeId.IndustrialEnergyConsumer:
    case AssetFamilyTypeId.MixedEnergyConsumer:
    case AssetFamilyTypeId.SiteNetConsumption:
    case AssetFamilyTypeId.SiteTotalConsumption:
      return IconEnergyConsumer
    case AssetFamilyTypeId.Generator:
      return IconGenerator
    case AssetFamilyTypeId.Hydro:
      return IconHydro
    case AssetFamilyTypeId.PowerGrid:
      return IconPowerGrid
    case AssetFamilyTypeId.SolarPv:
      return IconSolarPv
    case AssetFamilyTypeId.Wind:
      return IconWind
    default:
      return IconGenerator
  }
}

export function getAssetFamilyTypeMetaIcon(assetFamilyTypeId: string) {
  switch (assetFamilyTypeId) {
    case AssetFamilyTypeId.BatteryBankFlooded:
    case AssetFamilyTypeId.BatteryBankVRLA:
    case AssetFamilyTypeId.BatteryBankLFP:
    case AssetFamilyTypeId.BatteryBankNMC:
      return IconBatteryType
    case AssetFamilyTypeId.Generator:
      return IconFuel
    default:
      return ''
  }
}

export function getAssetFamilyTypeMetaLabel(assetFamilyTypeId: string) {
  // If translation function is extracted at module scope, this means it's evaluated once when the module loads.
  // This can cause issues with language switching at runtime since the function reference won't update when the locale changes.
  const { t } = i18n.global

  switch (assetFamilyTypeId) {
    case AssetFamilyTypeId.BatteryBankFlooded:
      return t('batteryBankType.flooded')
    case AssetFamilyTypeId.BatteryBankVRLA:
      return t('batteryBankType.VRLA')
    case AssetFamilyTypeId.BatteryBankLFP:
      return t('batteryBankType.LFP')
    case AssetFamilyTypeId.BatteryBankNMC:
      return t('batteryBankType.NMC')
    case AssetFamilyTypeId.Biogas:
      return t('biogas.singular')
    case AssetFamilyTypeId.Generator:
      return t('generators.singular')
    case AssetFamilyTypeId.Hydro:
      return t('hydro.singular')
    case AssetFamilyTypeId.PowerGrid:
      return t('powerGrids.singular')
    case AssetFamilyTypeId.SolarPv:
      return t('solarPvs.singular')
    case AssetFamilyTypeId.ResidentialEnergyConsumer:
    case AssetFamilyTypeId.CommercialEnergyConsumer:
    case AssetFamilyTypeId.IndustrialEnergyConsumer:
    case AssetFamilyTypeId.MixedEnergyConsumer:
    case AssetFamilyTypeId.SiteNetConsumption:
    case AssetFamilyTypeId.SiteTotalConsumption:
      return t('energyConsumers.singular')
    case AssetFamilyTypeId.Wind:
      return t('wind.singular')
    default:
      return ''
  }
}

export function getBatteryBankTypeName(assetFamilyTypeId: string) {
  const { t } = i18n.global

  switch (assetFamilyTypeId) {
    case AssetFamilyTypeId.BatteryBankFlooded:
      return t('batteryBankType.flooded')
    case AssetFamilyTypeId.BatteryBankVRLA:
      return t('batteryBankType.VRLA')
    case AssetFamilyTypeId.BatteryBankLFP:
      return t('batteryBankType.LFP')
    case AssetFamilyTypeId.BatteryBankNMC:
      return t('batteryBankType.NMC')
    default:
      return ''
  }
}

export function getAssetFamilyTypeName(assetFamilyTypeId: string) {
  const { t } = i18n.global

  switch (assetFamilyTypeId) {
    case AssetFamilyTypeId.BatteryBankVRLA:
      return `${t('batteryBankType.VRLA')} ${t('batteryBanks.singular')}`
    case AssetFamilyTypeId.BatteryBankFlooded:
      return `${t('batteryBankType.flooded')} ${t('batteryBanks.singular')}`
    case AssetFamilyTypeId.BatteryBankLFP:
      return `${t('batteryBankType.LFP')} ${t('batteryBanks.singular')}`
    case AssetFamilyTypeId.BatteryBankNMC:
      return `${t('batteryBankType.NMC')} ${t('batteryBanks.singular')}`
    case AssetFamilyTypeId.Biogas:
      return t('biogas.singular')
    case AssetFamilyTypeId.Generator:
      return t('generators.singular')
    case AssetFamilyTypeId.Hydro:
      return t('hydro.singular')
    case AssetFamilyTypeId.PowerGrid:
      return t('powerGrids.singular')
    case AssetFamilyTypeId.SolarPv:
      return t('solarPvs.singular')
    case AssetFamilyTypeId.ResidentialEnergyConsumer:
    case AssetFamilyTypeId.CommercialEnergyConsumer:
    case AssetFamilyTypeId.IndustrialEnergyConsumer:
    case AssetFamilyTypeId.MixedEnergyConsumer:
    case AssetFamilyTypeId.SiteNetConsumption:
    case AssetFamilyTypeId.SiteTotalConsumption:
      return t('energyConsumers.singular')
    case AssetFamilyTypeId.Wind:
      return t('wind.singular')
    default:
      return t('words.unknown')
  }
}

export function getAssetFamilyTypeParent(assetFamilyTypeId: string) {
  switch (assetFamilyTypeId) {
    case AssetFamilyTypeId.BatteryBankVRLA:
    case AssetFamilyTypeId.BatteryBankFlooded:
      return AssetFamilyTypeBatteryBanks.LeadAcid
    case AssetFamilyTypeId.BatteryBankLFP:
    case AssetFamilyTypeId.BatteryBankNMC:
      return AssetFamilyTypeBatteryBanks.Lithium
    default:
      return ''
  }
}

export function getAssetFamilyTypeRoute(assetFamilyId: string, assetFamilyTypeId: string): RouteLocationAsRelativeGeneric | undefined {
  const routeToConstruct = {
    name: '',
    params: {},
  }

  switch (assetFamilyTypeId) {
    case AssetFamilyTypeId.BatteryBankFlooded:
      routeToConstruct.name = AssetFamilyTypeRoutes.BatteryBank
      routeToConstruct.params = { batteryBankId: assetFamilyId, batteryBankType: AssetFamilyTypeBatteryBanks.LeadAcid }
      break
    case AssetFamilyTypeId.BatteryBankVRLA:
      routeToConstruct.name = AssetFamilyTypeRoutes.BatteryBank
      routeToConstruct.params = { batteryBankId: assetFamilyId, batteryBankType: AssetFamilyTypeBatteryBanks.LeadAcid }
      break
    case AssetFamilyTypeId.BatteryBankLFP:
      routeToConstruct.name = AssetFamilyTypeRoutes.BatteryBank
      routeToConstruct.params = { batteryBankId: assetFamilyId, batteryBankType: AssetFamilyTypeBatteryBanks.Lithium }
      break
    case AssetFamilyTypeId.BatteryBankNMC:
      routeToConstruct.name = AssetFamilyTypeRoutes.BatteryBank
      routeToConstruct.params = { batteryBankId: assetFamilyId, batteryBankType: AssetFamilyTypeBatteryBanks.Lithium }
      break
    case AssetFamilyTypeId.ResidentialEnergyConsumer:
    case AssetFamilyTypeId.CommercialEnergyConsumer:
    case AssetFamilyTypeId.IndustrialEnergyConsumer:
    case AssetFamilyTypeId.MixedEnergyConsumer:
    case AssetFamilyTypeId.SiteNetConsumption:
    case AssetFamilyTypeId.SiteTotalConsumption:
      routeToConstruct.name = AssetFamilyTypeRoutes.EnergyConsumer
      routeToConstruct.params = { energyConsumerId: assetFamilyId }
      break
    case AssetFamilyTypeId.Biogas:
      routeToConstruct.name = AssetFamilyTypeRoutes.Biogas
      routeToConstruct.params = { biogasId: assetFamilyId }
      break
    case AssetFamilyTypeId.Generator:
      routeToConstruct.name = AssetFamilyTypeRoutes.Generator
      routeToConstruct.params = { generatorId: assetFamilyId }
      break
    case AssetFamilyTypeId.Hydro:
      routeToConstruct.name = AssetFamilyTypeRoutes.Hydro
      routeToConstruct.params = { hydroId: assetFamilyId }
      break
    case AssetFamilyTypeId.PowerGrid:
      routeToConstruct.name = AssetFamilyTypeRoutes.PowerGrid
      routeToConstruct.params = { powerGridId: assetFamilyId }
      break
    case AssetFamilyTypeId.SolarPv:
      routeToConstruct.name = AssetFamilyTypeRoutes.SolarPv
      routeToConstruct.params = { solarPvId: assetFamilyId }
      break
    case AssetFamilyTypeId.Wind:
      routeToConstruct.name = AssetFamilyTypeRoutes.Wind
      routeToConstruct.params = { windId: assetFamilyId }
      break
    default:
      break
  }

  if (!routeToConstruct.name) {
    return undefined
  }

  return {
    name: routeToConstruct.name,
    params: routeToConstruct.params,
  } as RouteLocationAsRelativeGeneric
}

export const generationTypes = [
  AssetFamilyTypeId.Biogas,
  AssetFamilyTypeId.Generator,
  AssetFamilyTypeId.Hydro,
  AssetFamilyTypeId.PowerGrid,
  AssetFamilyTypeId.SolarPv,
  AssetFamilyTypeId.Wind,
]

export const generationAvoidingTypes = [
  AssetFamilyTypeId.Biogas,
  AssetFamilyTypeId.Hydro,
  AssetFamilyTypeId.SolarPv,
  AssetFamilyTypeId.Wind,
]
